/* ---------------------------------------------------------------------------
 * Author: Akarsh Singh
 * Date: 2026-08-19
 * Description: Added the Check RFQ Fit quick action. The button cannot know
 *   which RFQ is meant, so instead of sending a question with a hole in it the
 *   panel asks for the reference, then sends the coverage question with what the
 *   supplier typed. Also renders the RFQ priority bands in colour.
 * ------------------------------------------------------------------------- */
/* ---------------------------------------------------------------------------
 * Author: Akarsh Singh
 * Date: 2026-08-12
 * Description: Warm the AI backend as soon as the chat panel opens, and keep it
 *   warm while the panel stays open, so the user's first question does not pay
 *   for a Lambda cold start (see startWarmingAiBackend below).
 *
 * Author: Akarsh Singh
 * Date: 2026-06-20
 * Description: Made the floating chat launcher draggable vertically along the
 *   right edge — pointer-based drag with a click-vs-drag threshold, viewport
 *   clamping, and the chosen position persisted to localStorage — so it no
 *   longer overlaps page controls such as the table pager.
 * ------------------------------------------------------------------------- */
import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ChatbotAiService,
  ChatbotMessage,
  ChatbotOption,
  QuickPrompt,
  QuoteHistoryRfqType,
} from '../../services/chatbot-ai.service';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

interface PendingMessage extends ChatbotMessage {
  pending?: boolean;
}

@Component({
  selector: 'app-chatbot-ai',
  templateUrl: './chatbot-ai.component.html',
  styleUrls: ['./chatbot-ai.component.css'],
})
export class ChatbotAiComponent implements AfterViewInit, OnDestroy {
  @ViewChild('messageScroll') messageScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('editorBox') editorBox?: ElementRef<HTMLDivElement>;

  isOpen = false;
  isThinking = false;
  thinkingMessage = '';
  messages: PendingMessage[] = [];

  /** Set while the Check RFQ Fit button is waiting for the supplier to type a
   *  reference. The next thing they send is treated as that reference rather
   *  than as a question of its own. */
  private awaitingRfqReferenceForCoverage = false;

  /** Set once the buyer has picked Product or Service RFQ under Quote History.
   *  The next thing they send is the search term — an RFQ number, vessel name
   *  or PO number — not a question of its own. */
  private awaitingQuoteHistorySearch: QuoteHistoryRfqType | null = null;

  /** Placeholder for the message box; changes while the panel is waiting for a
   *  quote-history search term so the buyer knows what to type. */
  get editorPlaceholder(): string {
    return this.awaitingQuoteHistorySearch
      ? 'RFQ number, vessel name or PO number…'
      : 'Ask me anything about your procurement workflow…';
  }

  promptControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(500),
  ]);
  quickPrompts = this.chatbotService.getQuickPrompts();
  quickActions: QuickPrompt[] = [];
  showQuickActions = false;
  typingDots = ['.', '..', '...'];
  activeDotIndex = 0;
  private typingInterval: any;
  panelWidth = 441;
  panelHeight = 720;
  private readonly minWidth = 320;
  private readonly maxWidth = 520;
  private readonly minHeight = 380;
  private readonly maxHeight = 720;
  private isResizing = false;
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;

  /** Display label for the role badge in the header. */
  roleLabel = '';
  /** Lower-case role for css variants. */
  roleCss: 'buyer' | 'supplier' | 'admin' | 'guest' = 'guest';
  /** Icon shown on the toggle button + assistant avatars. */
  readonly botIconSrc = 'assets/favicon.ico';

  private readonly resizeMouseMove = (event: MouseEvent) => this.handleResizeMove(event);
  private readonly resizeMouseUp = () => this.stopResize();
  private routerSub?: Subscription;
  private openSub?: Subscription;

  // --- Vertical drag of the floating launcher along the right edge ---------
  // The launcher can be dragged up / down so it never sits on top of page
  // controls (e.g. the table pager). Only the vertical offset changes; the
  // button stays pinned to the right border. The chosen position is saved so
  // it survives navigation and reloads.
  fabBottom = 24; // resolved from storage in the constructor once fields exist
  private readonly fabStorageKey = 'chatbotFabBottom';
  private readonly fabDragThreshold = 5; // px before a press counts as a drag
  private readonly fabSafeGap = 12;      // min gap from the viewport edges
  private readonly fabSize = 64;         // launcher footprint used for clamping
  private fabDragging = false;
  private fabMoved = false;
  private fabDragStartY = 0;
  private fabDragStartBottom = 0;
  private readonly fabPointerMove = (event: PointerEvent) => this.onFabDragMove(event);
  private readonly fabPointerUp = (event: PointerEvent) => this.onFabDragEnd(event);

  // --- Keeping the AI backend warm while the panel is open -----------------
  // AWS reclaims an idle Lambda container after a few minutes, so a single
  // warm-up on open is not enough for a longer conversation. We re-ping on an
  // interval for as long as the panel is open, and stop the moment it closes so
  // a closed panel never generates background traffic.
  private aiWarmUpTimer: any = null;
  private readonly aiWarmUpIntervalMs = 4 * 60 * 1000;

  // Kept so the "Try again" button on a failed answer can re-send exactly what
  // was asked, rather than making the user retype it.
  private lastRequest?: () => Promise<ChatbotMessage>;
  private lastThinkingMessage = 'Typing...';

  constructor(private chatbotService: ChatbotAiService, private router: Router) {
    this.messages = this.chatbotService.getInitialMessages();
    const role = this.chatbotService.getUserRole();
    this.quickActions = this.chatbotService.getQuickActions(role);
    this.roleLabel = this.chatbotService.getRoleLabel();
    this.roleCss = ((role as any) === 'buyer' || (role as any) === 'supplier' || (role as any) === 'admin')
      ? (role as 'buyer' | 'supplier' | 'admin')
      : 'guest';

    // Track current route so service calls can include page context.
    this.chatbotService.setRouteContext(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd))
      .subscribe((e: any) => this.chatbotService.setRouteContext(e.urlAfterRedirects || e.url));

    // Allow the Help Center panel (or anything else) to open the assistant.
    this.openSub = this.chatbotService.openRequested$.subscribe(() => {
      this.isOpen = true;
      // Same warm-up as the launcher path — opening from the Help Center should
      // not leave the user waiting on a cold start either.
      this.startWarmingAiBackend();
      setTimeout(() => this.scrollToBottom(), 100);
    });

    // Restore the saved launcher position now that all clamp constants exist.
    this.fabBottom = this.loadFabBottom();
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  toggleQuickActions(): void {
    this.showQuickActions = !this.showQuickActions;
  }

  ngOnDestroy(): void {
    this.stopWarmingAiBackend();
    this.detachResizeListeners();
    window.removeEventListener('pointermove', this.fabPointerMove);
    window.removeEventListener('pointerup', this.fabPointerUp);
    this.routerSub?.unsubscribe();
    this.openSub?.unsubscribe();
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.startWarmingAiBackend();
      setTimeout(() => this.scrollToBottom(), 100);
    } else {
      this.stopWarmingAiBackend();
    }
  }

  /**
   * Starts warming the AI backend, and keeps it warm while the panel is open.
   *
   * Called the instant the panel opens rather than when the user sends their
   * first message, because the point is to spend the backend's start-up time
   * during the seconds the user is reading and typing. By the time they press
   * send the container is already up.
   *
   * Safe to call more than once — an existing timer is cleared first, so the
   * open-from-launcher and open-from-Help-Center paths cannot stack timers.
   */
  private startWarmingAiBackend(): void {
    this.stopWarmingAiBackend();
    this.chatbotService.warmUpAiBackend();
    this.aiWarmUpTimer = setInterval(
      () => this.chatbotService.warmUpAiBackend(),
      this.aiWarmUpIntervalMs,
    );
  }

  /**
   * Stops the warm-up interval when the panel closes or the component is
   * destroyed, so a closed chat panel never keeps making background requests.
   */
  private stopWarmingAiBackend(): void {
    if (this.aiWarmUpTimer) {
      clearInterval(this.aiWarmUpTimer);
      this.aiWarmUpTimer = null;
    }
  }

  // ===========================================================================
  // Launcher vertical drag
  // ===========================================================================

  /** Read the saved launcher offset, falling back to the default corner. */
  private loadFabBottom(): number {
    try {
      const saved = Number(localStorage.getItem('chatbotFabBottom'));
      if (!isNaN(saved) && saved > 0) {
        return this.clampFabBottom(saved);
      }
    } catch {
      // localStorage may be unavailable (private mode) — use the default.
    }
    return 24;
  }

  /** Keep the launcher fully on-screen regardless of viewport height. */
  private clampFabBottom(value: number): number {
    const max = Math.max(24, window.innerHeight - this.fabSize - this.fabSafeGap);
    return Math.min(max, Math.max(this.fabSafeGap, value));
  }

  /** Begin a potential drag. We do not yet know if this is a click or a drag
   *  until the pointer moves past the threshold. */
  onFabPointerDown(event: PointerEvent): void {
    if (event.button != null && event.button !== 0) {
      return; // ignore non-primary buttons
    }
    this.fabDragging = true;
    this.fabMoved = false;
    this.fabDragStartY = event.clientY;
    this.fabDragStartBottom = this.fabBottom;
    window.addEventListener('pointermove', this.fabPointerMove);
    window.addEventListener('pointerup', this.fabPointerUp);
  }

  private onFabDragMove(event: PointerEvent): void {
    if (!this.fabDragging) {
      return;
    }
    // Pointer moving up (smaller clientY) should raise the launcher, so the
    // bottom offset grows.
    const deltaY = this.fabDragStartY - event.clientY;
    if (!this.fabMoved && Math.abs(deltaY) > this.fabDragThreshold) {
      this.fabMoved = true;
    }
    if (this.fabMoved) {
      this.fabBottom = this.clampFabBottom(this.fabDragStartBottom + deltaY);
    }
  }

  private onFabDragEnd(event: PointerEvent): void {
    if (!this.fabDragging) {
      return;
    }
    this.fabDragging = false;
    window.removeEventListener('pointermove', this.fabPointerMove);
    window.removeEventListener('pointerup', this.fabPointerUp);
    // Persist only when the launcher was actually moved. fabMoved is left set
    // so the click that follows the release can be suppressed (see onFabClick).
    if (this.fabMoved) {
      try {
        localStorage.setItem(this.fabStorageKey, String(this.fabBottom));
      } catch {
        // ignore storage failures
      }
    }
  }

  /** The browser fires a click right after a drag's pointerup; swallow it when
   *  the launcher was dragged so the chat panel does not open/close by accident.
   *  A genuine tap (no movement) falls through to toggle(). */
  onFabClick(event: MouseEvent): void {
    if (this.fabMoved) {
      event.preventDefault();
      event.stopPropagation();
      this.fabMoved = false;
      return;
    }
    this.toggle();
  }

  /** Re-clamp the launcher if the window shrinks so it cannot end up off-screen. */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.fabBottom = this.clampFabBottom(this.fabBottom);
  }

  handleEnter(event: KeyboardEvent | Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey) {
      return;
    }
    if (typeof keyboardEvent.preventDefault === 'function') {
      keyboardEvent.preventDefault();
    }
    this.submitPrompt();
  }

  submitPrompt(): void {
    if (this.promptControl.invalid || this.isThinking) {
      return;
    }
    const question = this.promptControl.value?.trim();
    if (!question) {
      return;
    }
    // What the user TYPED is sent to the assistant unchanged. This used to set
    // promptId to 'overview' whenever the text mentioned a dashboard, an
    // overview or analytics, and the service then substituted a canned question
    // for it — so "profile overview" was answered with a dashboard summary the
    // user never asked for. That substitution belongs to BUTTON clicks, which
    // have no typed words to preserve, and to nothing else.
    //
    // The dashboard wording is still used for the "thinking" line, since that
    // only affects what is shown while waiting.
    // The supplier is answering "which RFQ?", so what they typed is a
    // reference, not a question. It is shown as they typed it and sent as the
    // question the button stands for.
    if (this.awaitingRfqReferenceForCoverage) {
      this.awaitingRfqReferenceForCoverage = false;
      this.pushUserMessage(question);
      this.promptControl.reset();
      this.resetEditor();
      this.scrollToBottom();
      this.pushAssistantResponse(undefined, 1000, `Can I supply ${question}?`);
      return;
    }

    // The buyer is answering "Enter RFQ Number, Vessel Name or PO Number", so
    // what they typed is the search term.
    if (this.awaitingQuoteHistorySearch) {
      const rfqType = this.awaitingQuoteHistorySearch;
      this.awaitingQuoteHistorySearch = null;
      this.pushUserMessage(question);
      this.promptControl.reset();
      this.resetEditor();
      this.scrollToBottom();
      this.runAssistantRequest(
        () => this.chatbotService.searchQuoteHistory(rfqType, question),
        'Searching quotation history',
      );
      return;
    }

    // Typing "quote history" starts the same guided flow as the button.
    if (
      this.chatbotService.getUserRole() === 'buyer' &&
      /\bquot(e|ation)s?\s+history\b/i.test(question)
    ) {
      this.pushUserMessage(question);
      this.promptControl.reset();
      this.resetEditor();
      this.startQuoteHistoryFlow();
      return;
    }

    const looksLikeADashboardQuestion = this.isDashboardQuery(question);
    this.pushUserMessage(question);
    this.promptControl.reset();
    this.resetEditor();
    this.scrollToBottom();

    this.pushAssistantResponse(undefined, 1000, question, looksLikeADashboardQuestion);
  }

  runQuickPrompt(promptId: string, promptText: string): void {
    if (this.isThinking) {
      return;
    }

    // For supplier quick action: navigate directly to RFQ list filtered to unactioned
    if (promptId === 'qa-respond-rfq' && this.chatbotService.getUserRole() === 'supplier') {
      this.router.navigate(['/operations/supplier-rfq-list'], {
        queryParams: {
          updatedStatus: 'Unactioned',
          selectedStatus: 'Unactioned',
          label: 'RFQ',
        },
      });
      return;
    }

    // Checking an RFQ needs a reference, and the button has no way to know
    // which one. Rather than sending a question with a hole in it, the panel
    // asks for the reference and treats whatever comes back as the answer to
    // that question — see submitPrompt.
    if (promptId === 'qa-rfq-coverage') {
      this.awaitingRfqReferenceForCoverage = true;
      this.pushAssistantPrompt(
        'Which RFQ shall I check? Paste the reference, for example RFQ-VSMPS-20260806-2433.'
      );
      this.focusEditor();
      return;
    }

    // Quote History needs to know the RFQ kind and what to search for, so the
    // panel asks for both before anything goes to the backend.
    if (promptId === 'qa-quote-history') {
      this.startQuoteHistoryFlow();
      return;
    }

    this.pushUserMessage(promptText);
    this.promptControl.reset();
    this.resetEditor();
    this.scrollToBottom();
    this.pushAssistantResponse(promptId, 600, promptText);
  }

  /** Step one of Quote History: offer Product RFQ / Service RFQ. */
  private startQuoteHistoryFlow(): void {
    this.awaitingRfqReferenceForCoverage = false;
    this.awaitingQuoteHistorySearch = null;
    this.messages = [...this.messages, this.chatbotService.buildQuoteHistoryRfqTypePrompt()];
    this.scrollToBottom();
  }

  /**
   * Handles a click on one of the option buttons inside an assistant bubble.
   * Each set of options can be answered once; the buttons lock afterwards.
   */
  chooseOption(message: PendingMessage, option: ChatbotOption): void {
    if (this.isThinking || message.selectedOption) {
      return;
    }
    message.selectedOption = option.value;
    this.pushUserMessage(option.label);

    if (message.optionsFor === 'quote-history-rfq-type') {
      this.awaitingRfqReferenceForCoverage = false;
      this.awaitingQuoteHistorySearch = option.value as QuoteHistoryRfqType;
      this.pushAssistantPrompt('Enter RFQ Number, Vessel Name or PO Number.');
      this.focusEditor();
      return;
    }

    if (message.optionsFor === 'quote-history-rfq' && message.optionContext) {
      const { rfqType, searchTerm } = message.optionContext;
      this.scrollToBottom();
      this.runAssistantRequest(
        () => this.chatbotService.searchQuoteHistory(rfqType, searchTerm, { rfqId: option.value }),
        'Loading the suppliers who quoted',
      );
      return;
    }

    if (message.optionsFor === 'quote-history-supplier' && message.optionContext) {
      const { rfqType, searchTerm, rfqId } = message.optionContext;
      this.scrollToBottom();
      this.runAssistantRequest(
        () => this.chatbotService.searchQuoteHistory(rfqType, searchTerm, { rfqId, supplierId: option.value }),
        'Loading quotation history',
      );
    }
  }

  /**
   * Puts a short assistant line into the conversation without calling the
   * backend. Used when the panel needs something from the user before it can
   * ask a question worth sending.
   */
  private pushAssistantPrompt(content: string): void {
    this.messages = [
      ...this.messages,
      {
        id: `local-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        tone: 'info',
      } as PendingMessage,
    ];
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    if (!this.messageScroll) {
      return;
    }
    requestAnimationFrame(() => {
      const nativeEl = this.messageScroll?.nativeElement;
      if (!nativeEl) {
        return;
      }
      nativeEl.scrollTop = nativeEl.scrollHeight;
    });
  }

  private pushUserMessage(content: string): void {
    const newMessage: PendingMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };
    this.messages.push(newMessage);
    this.chatbotService.pushHistory('user', content);
  }

  /**
   * Identity function for the deep-link list, so Angular does not rebuild every
   * button each time a message re-renders.
   */
  trackByLinkLabel(_index: number, link: { label: string }): string {
    return link.label;
  }

  /** Identity function for the timing breakdown rows. */
  trackByStepLabel(_index: number, step: { label: string }): string {
    return step.label;
  }

  /**
   * Formats a duration the way a person reads it rather than the way a computer
   * stores it: milliseconds while they are small enough to matter, seconds once
   * they are not.
   */
  formatDuration(milliseconds?: number | null): string {
    const safeMilliseconds = Number(milliseconds) || 0;
    if (safeMilliseconds < 1000) {
      return `${Math.round(safeMilliseconds)}ms`;
    }
    return `${(safeMilliseconds / 1000).toFixed(2)}s`;
  }

  /**
   * Width of one bar in the timing breakdown, as a percentage of the slowest
   * thing in that answer.
   *
   * Scaling to the slowest step rather than to the total is deliberate: it makes
   * the bottleneck obvious. Against the total, a 60ms step and a 6ms step would
   * both be invisible slivers and you could not tell them apart.
   */
  barWidthPercent(
    milliseconds: number | null | undefined,
    timing: { steps: Array<{ milliseconds: number }>; networkMs: number; containerStartupMs?: number | null },
  ): number {
    const value = Number(milliseconds) || 0;
    const slowest = Math.max(
      1,
      timing.networkMs || 0,
      timing.containerStartupMs || 0,
      ...timing.steps.map((step) => step.milliseconds || 0),
    );
    return Math.max(2, Math.round((value / slowest) * 100));
  }

  /**
   * Re-sends the last question after a failed request.
   *
   * Only offered when the request never reached the backend, so retrying is
   * genuinely worth a try rather than a way to hit the same error twice.
   */
  retryLastQuestion(): void {
    if (this.isThinking || !this.lastRequest) {
      return;
    }
    this.runAssistantRequest(this.lastRequest, this.lastThinkingMessage, 200);
  }

  private pushAssistantResponse(
    promptId?: string,
    delay = 1000,
    question?: string,
    showDashboardThinkingLine = false
  ): void {
    // Matching an RFQ against the catalogue reads the RFQ's line items, goes
    // through the whole product list and scores every line, so it takes
    // noticeably longer than a lookup. Saying "Typing..." for fifteen seconds
    // reads as a hang; saying what is happening reads as work.
    const isCoverageCheck = this.looksLikeAnRfqCoverageQuestion(question);
    const thinkingMessage = isCoverageCheck
      ? 'Reading the RFQ line items, searching your products and scoring each match — this takes a little longer'
      : promptId === 'overview' || showDashboardThinkingLine
      ? 'Analyzing your dashboard'
      : 'Typing...';
    this.runAssistantRequest(
      () => this.chatbotService.generateResponseFromApi(promptId, question),
      thinkingMessage,
      delay,
    );
  }

  /**
   * Shows the typing indicator, waits for one assistant reply and appends it.
   * Remembers the request so "Try again" can repeat it exactly.
   */
  private runAssistantRequest(
    request: () => Promise<ChatbotMessage>,
    thinkingMessage = 'Typing...',
    delay = 600,
  ): void {
    this.lastRequest = request;
    this.lastThinkingMessage = thinkingMessage;
    this.isThinking = true;
    this.thinkingMessage = thinkingMessage;
    this.startTypingAnimation();
    setTimeout(async () => {
      const response = await request();
      this.messages.push(response);
      this.chatbotService.pushHistory('assistant', response?.content || '');
      this.isThinking = false;
      this.thinkingMessage = '';
      this.stopTypingAnimation();
      this.scrollToBottom();
    }, delay);
  }


  /**
   * Says whether a question is asking how well the catalogue covers an RFQ.
   *
   * Only used to choose the waiting line, so a wrong guess costs nothing worse
   * than a less specific message. The real decision about how long to allow is
   * made on the server by the tool itself.
   */
  private looksLikeAnRfqCoverageQuestion(question?: string): boolean {
    if (!question) {
      return false;
    }
    const asked = question.toLowerCase();
    const aboutSupplying =
      asked.includes('can i supply') ||
      asked.includes('can i cover') ||
      asked.includes('do i have the products') ||
      asked.includes('match') ||
      asked.includes('coverage');
    return aboutSupplying && asked.includes('rfq');
  }

  startResize(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.panelWidth;
    this.resizeStartHeight = this.panelHeight;
    window.addEventListener('mousemove', this.resizeMouseMove);
    window.addEventListener('mouseup', this.resizeMouseUp);
  }

  private handleResizeMove(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }
    const deltaX = this.resizeStartX - event.clientX;
    const deltaY = event.clientY - this.resizeStartY;
    const newWidth = this.resizeStartWidth + deltaX;
    const newHeight = this.resizeStartHeight - deltaY;
    this.panelWidth = Math.min(
      this.maxWidth,
      Math.max(this.minWidth, newWidth),
    );
    this.panelHeight = Math.min(
      this.maxHeight,
      Math.max(this.minHeight, newHeight),
    );
  }

  private stopResize(): void {
    if (!this.isResizing) {
      return;
    }
    this.isResizing = false;
    this.detachResizeListeners();
  }

  private detachResizeListeners(): void {
    window.removeEventListener('mousemove', this.resizeMouseMove);
    window.removeEventListener('mouseup', this.resizeMouseUp);
  }

  private isDashboardQuery(text?: string): boolean {
    if (!text) return false;
    const lowered = text.toLowerCase();
    return lowered.includes('dashboard') || lowered.includes('overview') || lowered.includes('analytics');
  }

  openNotifications(): void {
    this.router.navigate(['/notifications-list']);
  }

  openProducts(): void {
    this.router.navigate(['/search/products']);
  }

  navigateLink(link?: { label: string; route: any[]; queryParams?: Record<string, any> }): void {
    if (!link) return;
    this.router.navigate(link.route, { queryParams: link.queryParams });
  }

  onEditorInput(event: Event): void {
    const target = event.target as HTMLDivElement;
    const text = target?.innerText || '';
    this.promptControl.setValue(text.trimStart());
    this.promptControl.markAsTouched();
  }

  private resetEditor(): void {
    if (this.editorBox?.nativeElement) {
      this.editorBox.nativeElement.innerText = '';
    }
  }

  /**
   * Puts the cursor in the message box.
   *
   * Used after the panel asks a question of its own — having just asked which
   * RFQ to check, leaving the user to find and click the box themselves would
   * be a poor way to ask.
   */
  private focusEditor(): void {
    // After the view has settled, or the box may not be focusable yet.
    setTimeout(() => this.editorBox?.nativeElement?.focus(), 0);
  }

  private startTypingAnimation(): void {
    this.stopTypingAnimation();
    this.typingInterval = setInterval(() => {
      this.activeDotIndex = (this.activeDotIndex + 1) % this.typingDots.length;
    }, 400);
  }

  private stopTypingAnimation(): void {
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
      this.activeDotIndex = 0;
    }
  }

}
